import { useState, useEffect } from 'react'
import { Box, Typography, Chip, Collapse, IconButton, Tooltip, TextField, CircularProgress } from '@mui/material'
import { ExpandMore, ExpandLess, AccountTree, Check } from '@mui/icons-material'
import { analysisApi } from '../services/api'

interface CostFactorsBreakdownProps {
  estimate: any
  // Called after a volume assumption is saved/cleared so the parent can
  // re-fetch and show the updated cost_per_day_usd immediately — no
  // rescan needed, per the confirmed design (§22, 2026-07-27).
  onVolumeAssumptionChanged?: () => void
}

/**
 * §22 accuracy overhaul — surfaces WHY a cost estimate is what it is,
 * not just the number. Mirrors AgentABFDetail.tsx's always-visible-chips
 * + expandable-detail pattern.
 */
const CostFactorsBreakdown = ({ estimate, onVolumeAssumptionChanged }: CostFactorsBreakdownProps) => {
  const [expanded, setExpanded] = useState(false)
  const [volumeInput, setVolumeInput] = useState('')
  const [savingVolume, setSavingVolume] = useState(false)
  const [volumeError, setVolumeError] = useState<string | null>(null)
  const [justSaved, setJustSaved] = useState(false)

  useEffect(() => {
    setVolumeInput(
      estimate?.requests_per_day_assumption != null ? String(estimate.requests_per_day_assumption) : '',
    )
  }, [estimate?.requests_per_day_assumption])

  const factors = estimate?.cost_factors
  if (!factors) return null

  const isEntryPoint = estimate.is_entry_point
  const agentIdHint = estimate.agent_id_hint

  const saveVolumeAssumption = async () => {
    if (!agentIdHint) return
    const n = parseInt(volumeInput, 10)
    if (!Number.isFinite(n) || n <= 0) {
      setVolumeError('Enter a positive number')
      return
    }
    setSavingVolume(true)
    setVolumeError(null)
    try {
      await analysisApi.upsertAgentVolumeAssumption(agentIdHint, n)
      setJustSaved(true)
      setTimeout(() => setJustSaved(false), 2000)
      onVolumeAssumptionChanged?.()
    } catch (e: any) {
      setVolumeError(e?.response?.data?.detail || 'Failed to save')
    } finally {
      setSavingVolume(false)
    }
  }

  return (
    <Box sx={{ mt: 0.5 }}>
      {/* Always-visible summary */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
        {isEntryPoint === true && (
          <Tooltip title="Nothing else in this scan calls this agent — its cost counts toward the scan total.">
            <Chip icon={<AccountTree sx={{ fontSize: 12 }} />} label="Entry point" size="small"
              color="success" variant="outlined" sx={{ fontSize: '0.6rem', height: 20 }} />
          </Tooltip>
        )}
        {isEntryPoint === false && (
          <Tooltip title="Called by other agents in this scan — its cost is already folded into the caller's fan-out-adjusted estimate, so it's excluded from the scan total to avoid double-counting.">
            <Chip icon={<AccountTree sx={{ fontSize: 12 }} />} label="Delegate / internal hop" size="small"
              variant="outlined" sx={{ fontSize: '0.6rem', height: 20 }} />
          </Tooltip>
        )}
        {typeof estimate.downstream_count === 'number' && estimate.downstream_count > 0 && (
          <Tooltip title="Real, deduplicated number of agents/servers this agent's own call graph reaches — computed from actual resolved delegation/MCP/A2A edges, not a raw connection-list count.">
            <Chip label={`${estimate.downstream_count} real fan-out hop${estimate.downstream_count !== 1 ? 's' : ''}`}
              size="small" variant="outlined" sx={{ fontSize: '0.6rem', height: 20 }} />
          </Tooltip>
        )}
      </Box>

      {/* Expected daily requests — the only way cost/day can ever populate;
          ARIAS is pre-production and has no visibility into real traffic. */}
      {agentIdHint && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
          <Tooltip title="Expected daily requests for this agent — a stated assumption, not a measurement (ARIAS is pre-production). Drives Projected cost / day above; updates immediately on save, no rescan needed.">
            <TextField
              size="small" type="number" variant="outlined" placeholder="Expected requests/day"
              value={volumeInput}
              onChange={(e) => { setVolumeInput(e.target.value); setVolumeError(null) }}
              onKeyDown={(e) => { if (e.key === 'Enter') saveVolumeAssumption() }}
              inputProps={{ min: 1, style: { fontSize: '0.7rem', padding: '4px 8px', width: 110 } }}
            />
          </Tooltip>
          <Tooltip title="Save">
            <IconButton size="small" onClick={saveVolumeAssumption} disabled={savingVolume} sx={{ p: 0.25 }}>
              {savingVolume
                ? <CircularProgress size={14} />
                : <Check sx={{ fontSize: 14, color: justSaved ? 'success.main' : undefined }} />}
            </IconButton>
          </Tooltip>
          {volumeError && (
            <Typography variant="caption" color="error">{volumeError}</Typography>
          )}
        </Box>
      )}

      {/* Expandable details */}
      <Box sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer', mt: 0.5 }}
        onClick={() => setExpanded(!expanded)}>
        <IconButton size="small">
          {expanded ? <ExpandLess sx={{ fontSize: 14 }} /> : <ExpandMore sx={{ fontSize: 14 }} />}
        </IconButton>
        <Typography variant="caption" color="text.secondary">
          {expanded ? 'Hide' : 'Show'} cost factors
        </Typography>
      </Box>

      <Collapse in={expanded}>
        <Box sx={{ pl: 2, pt: 0.5, pb: 0.5, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
          <Tooltip title={factors.iteration_cap?.is_real
            ? 'Real max_iterations/max_turns value found in the source code.'
            : 'No real iteration cap found in the source — the estimate uses a default wide-range multiplier for this loop-shaped pattern.'}>
            <Chip
              label={factors.iteration_cap?.is_real
                ? `Iteration cap: ${factors.iteration_cap.value} (declared)`
                : 'Iteration cap: default (unknown)'}
              size="small" variant="outlined"
              color={factors.iteration_cap?.is_real ? 'success' : 'default'}
              sx={{ fontSize: '0.6rem', height: 20 }}
            />
          </Tooltip>

          <Tooltip title={factors.goal_text_tokens?.is_real
            ? 'Estimated from this agent\'s real, measured prompt/instruction text length.'
            : 'No prompt text length available — the estimate uses a fixed default floor for this architecture pattern.'}>
            <Chip
              label={factors.goal_text_tokens?.is_real
                ? `Prompt: ~${factors.goal_text_tokens.value} tokens (measured)`
                : 'Prompt size: default'}
              size="small" variant="outlined"
              color={factors.goal_text_tokens?.is_real ? 'success' : 'default'}
              sx={{ fontSize: '0.6rem', height: 20 }}
            />
          </Tooltip>

          {factors.memory_bounded === false && (
            <Tooltip title="Unbounded conversation memory detected — in a multi-turn loop, each turn resends the entire growing history, so cost grows cumulatively across iterations, not just per-turn.">
              <Chip label="Memory: unbounded" size="small" color="warning" variant="outlined"
                sx={{ fontSize: '0.6rem', height: 20 }} />
            </Tooltip>
          )}
          {factors.memory_bounded === true && (
            <Tooltip title="Bounded/windowed conversation memory detected — cost per turn stays roughly constant across the loop.">
              <Chip label="Memory: bounded" size="small" color="success" variant="outlined"
                sx={{ fontSize: '0.6rem', height: 20 }} />
            </Tooltip>
          )}

          {(factors.tool_classification?.large_payload > 0 || factors.tool_classification?.small_payload > 0) && (
            <Tooltip title="Tools classified by name pattern into large-payload (search/browse/read/query-style — injects more tokens back into context) vs small-payload (calculators, status checks).">
              <Chip
                label={`${factors.tool_classification.large_payload} large / ${factors.tool_classification.small_payload} small-payload tools`}
                size="small" variant="outlined" sx={{ fontSize: '0.6rem', height: 20 }}
              />
            </Tooltip>
          )}

          {factors.context_window_capped && (
            <Tooltip title="The estimate's upper bound was capped to this model's actual context window — the uncapped heuristic exceeded what the model could physically accept.">
              <Chip label="Capped to model context window" size="small" color="info" variant="outlined"
                sx={{ fontSize: '0.6rem', height: 20 }} />
            </Tooltip>
          )}

          {factors.has_max_tokens === false && (
            <Tooltip title="No output token limit found on this agent's LLM call — output length is unbounded on top of any iteration-count uncertainty.">
              <Chip label="No output token limit" size="small" color="warning" variant="outlined"
                sx={{ fontSize: '0.6rem', height: 20 }} />
            </Tooltip>
          )}
        </Box>
      </Collapse>
    </Box>
  )
}

export default CostFactorsBreakdown
